import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import api from "../api/axios";


const buildBrowserDetails = () => {
    if (
        typeof window === "undefined" ||
        typeof navigator === "undefined"
    ) {
        return {};
    }

    return {
        timezone:
            Intl.DateTimeFormat()
                .resolvedOptions()
                .timeZone ||
            null,

        language:
            navigator.language ||
            null,

        languages:
            Array.isArray(
                navigator.languages
            )
                ? navigator.languages
                : [],

        platform:
            navigator.platform ||
            null,

        vendor:
            navigator.vendor ||
            null,

        screen_width:
            window.screen
                ?.width ||
            null,

        screen_height:
            window.screen
                ?.height ||
            null,

        color_depth:
            window.screen
                ?.colorDepth ||
            null,

        pixel_ratio:
            window.devicePixelRatio ||
            1,

        hardware_concurrency:
            navigator
                .hardwareConcurrency ||
            null,

        device_memory:
            navigator
                .deviceMemory ||
            null,

        max_touch_points:
            navigator
                .maxTouchPoints ||
            0,
    };
};


const createEventId = () => {
    if (
        typeof crypto !==
            "undefined" &&
        typeof crypto.randomUUID ===
            "function"
    ) {
        return crypto.randomUUID();
    }

    return (
        `${Date.now()}-` +
        Math.random()
            .toString(36)
            .slice(2)
    );
};


/*
 * Despite the historical name, this hook now handles:
 *
 * - guest limits
 * - authenticated tier-0 daily limits
 * - tier 1+ unlimited access
 *
 * The BACKEND decides the real tier.
 */
const useGuestExploreGuard = (
    isAuthenticated,
    tierlevel
) => {
    const [
        exploreGuardReady,
        setExploreGuardReady,
    ] =
        useState(false);

    const [
        accessMode,
        setAccessMode,
    ] =
        useState(
            "checking"
        );

    const [
        serverTierLevel,
        setServerTierLevel,
    ] =
        useState(null);

    const [
        totalClickCount,
        setTotalClickCount,
    ] =
        useState(0);

    const [
        totalLimit,
        setTotalLimit,
    ] =
        useState(15);

    const [
        pageClicks,
        setPageClicks,
    ] =
        useState({
            shop: 0,
            product_details: 0,
        });

    const [
        pageLimits,
        setPageLimits,
    ] =
        useState({
            shop: 10,
            product_details: 5,
        });

    const [
        globallyLocked,
        setGloballyLocked,
    ] =
        useState(false);

    const [
        daily,
        setDaily,
    ] =
        useState(false);

    const [
        usageDate,
        setUsageDate,
    ] =
        useState(null);

    const [
        verificationFailed,
        setVerificationFailed,
    ] =
        useState(false);


    const mountedRef =
        useRef(true);


    const browserDetails =
        useMemo(
            () =>
                buildBrowserDetails(),
            []
        );


    useEffect(() => {
        mountedRef.current =
            true;

        return () => {
            mountedRef.current =
                false;
        };
    }, []);


    const applyServerStatus =
        useCallback(
            (data) => {
                if (
                    !mountedRef.current
                ) {
                    return;
                }


                const mode =
                    data?.mode ||
                    (
                        data?.exempt
                            ? "unlimited"
                            : "guest"
                    );


                setAccessMode(
                    mode
                );


                setServerTierLevel(
                    data
                        ?.tier_level ??
                    null
                );


                /*
                 * Tier 1+:
                 * no browsing limits.
                 */
                if (
                    data?.exempt ||
                    mode ===
                        "unlimited"
                ) {
                    setTotalClickCount(
                        0
                    );

                    setTotalLimit(
                        Number.MAX_SAFE_INTEGER
                    );

                    setPageClicks({
                        shop:
                            0,

                        product_details:
                            0,
                    });

                    setPageLimits({
                        shop:
                            Number.MAX_SAFE_INTEGER,

                        product_details:
                            Number.MAX_SAFE_INTEGER,
                    });

                    setGloballyLocked(
                        false
                    );

                    setDaily(
                        false
                    );

                    setUsageDate(
                        null
                    );

                    setVerificationFailed(
                        false
                    );

                    setExploreGuardReady(
                        true
                    );

                    return;
                }


                const nextTotalLimit =
                    Math.max(
                        1,
                        Number(
                            data?.limits
                                ?.total ??
                            data?.limit ??
                            (
                                mode ===
                                    "authenticated_free"
                                    ? 50
                                    : 15
                            )
                        )
                    );


                const nextShopLimit =
                    Math.max(
                        1,
                        Number(
                            data?.limits
                                ?.shop ??
                            (
                                mode ===
                                    "authenticated_free"
                                    ? 30
                                    : 10
                            )
                        )
                    );


                const nextDetailsLimit =
                    Math.max(
                        1,
                        Number(
                            data?.limits
                                ?.product_details ??
                            (
                                mode ===
                                    "authenticated_free"
                                    ? 20
                                    : 5
                            )
                        )
                    );


                const nextTotalCount =
                    Math.max(
                        0,
                        Number(
                            data?.click_count ||
                            0
                        )
                    );


                const nextShopCount =
                    Math.max(
                        0,
                        Number(
                            data?.page_clicks
                                ?.shop ||
                            0
                        )
                    );


                const nextDetailsCount =
                    Math.max(
                        0,
                        Number(
                            data?.page_clicks
                                ?.product_details ||
                            0
                        )
                    );


                setTotalLimit(
                    nextTotalLimit
                );

                setPageLimits({
                    shop:
                        nextShopLimit,

                    product_details:
                        nextDetailsLimit,
                });


                /*
                 * Never let an older response reduce a count.
                 */
                setTotalClickCount(
                    (previous) =>
                        Math.max(
                            previous,
                            nextTotalCount
                        )
                );


                setPageClicks(
                    (previous) => ({
                        shop:
                            Math.max(
                                previous
                                    .shop ||
                                0,
                                nextShopCount
                            ),

                        product_details:
                            Math.max(
                                previous
                                    .product_details ||
                                0,
                                nextDetailsCount
                            ),
                    })
                );


                setGloballyLocked(
                    Boolean(
                        data?.locked
                    ) ||
                    nextTotalCount >=
                        nextTotalLimit
                );


                setDaily(
                    Boolean(
                        data?.daily
                    )
                );


                setUsageDate(
                    data
                        ?.usage_date ||
                    null
                );


                setVerificationFailed(
                    false
                );


                setExploreGuardReady(
                    true
                );
            },
            []
        );


    useEffect(() => {
        let cancelled =
            false;


        /*
         * Re-check whenever auth/tier context changes.
         *
         * The tierlevel argument is ONLY a trigger for re-checking.
         * Enforcement still comes from the backend.
         */
        setExploreGuardReady(
            false
        );

        setVerificationFailed(
            false
        );


        const checkStatus =
            async () => {
                try {
                    const response =
                        await api.post(
                            "/guestExploreRoutes/status",
                            {
                                browser:
                                    browserDetails,
                            },
                            {
                                withCredentials:
                                    true,
                            }
                        );


                    if (
                        cancelled
                    ) {
                        return;
                    }


                    applyServerStatus(
                        response.data
                    );
                } catch (error) {
                    console.error(
                        "Explore access verification failed:",
                        error
                    );


                    if (
                        cancelled ||
                        !mountedRef.current
                    ) {
                        return;
                    }


                    /*
                     * Fail closed for limited users.
                     */
                    setVerificationFailed(
                        true
                    );

                    setGloballyLocked(
                        true
                    );

                    setExploreGuardReady(
                        true
                    );
                }
            };


        checkStatus();


        return () => {
            cancelled =
                true;
        };
    }, [
        isAuthenticated,
        tierlevel,
        browserDetails,
        applyServerStatus,
    ]);


    const isUnlimited =
        accessMode ===
        "unlimited";


    const getPageStatus =
        useCallback(
            (
                page
            ) => {
                if (
                    isUnlimited
                ) {
                    return {
                        locked:
                            false,

                        reason:
                            null,

                        clickCount:
                            0,

                        limit:
                            null,

                        totalClickCount:
                            0,

                        totalLimit:
                            null,

                        daily:
                            false,

                        mode:
                            accessMode,
                    };
                }


                if (
                    globallyLocked
                ) {
                    return {
                        locked:
                            true,

                        reason:
                            "total",

                        clickCount:
                            totalClickCount,

                        limit:
                            totalLimit,

                        totalClickCount,

                        totalLimit,

                        daily,

                        mode:
                            accessMode,
                    };
                }


                if (
                    page ===
                    "shop"
                ) {
                    const clickCount =
                        Number(
                            pageClicks
                                .shop ||
                            0
                        );

                    const limit =
                        Number(
                            pageLimits
                                .shop ||
                            (
                                accessMode ===
                                    "authenticated_free"
                                    ? 30
                                    : 10
                            )
                        );


                    const locked =
                        clickCount >=
                        limit;


                    return {
                        locked,

                        reason:
                            locked
                                ? "shop"
                                : null,

                        clickCount,

                        limit,

                        totalClickCount,

                        totalLimit,

                        daily,

                        mode:
                            accessMode,
                    };
                }


                if (
                    page ===
                    "product_details"
                ) {
                    const clickCount =
                        Number(
                            pageClicks
                                .product_details ||
                            0
                        );

                    const limit =
                        Number(
                            pageLimits
                                .product_details ||
                            (
                                accessMode ===
                                    "authenticated_free"
                                    ? 20
                                    : 5
                            )
                        );


                    const locked =
                        clickCount >=
                        limit;


                    return {
                        locked,

                        reason:
                            locked
                                ? "product_details"
                                : null,

                        clickCount,

                        limit,

                        totalClickCount,

                        totalLimit,

                        daily,

                        mode:
                            accessMode,
                    };
                }


                return {
                    locked:
                        false,

                    reason:
                        null,

                    clickCount:
                        0,

                    limit:
                        0,

                    totalClickCount,

                    totalLimit,

                    daily,

                    mode:
                        accessMode,
                };
            },
            [
                isUnlimited,
                globallyLocked,
                totalClickCount,
                totalLimit,
                pageClicks,
                pageLimits,
                daily,
                accessMode,
            ]
        );


    const registerExploreClick =
        useCallback(
            async (
                page
            ) => {
                if (
                    isUnlimited
                ) {
                    return;
                }


                const pageStatus =
                    getPageStatus(
                        page
                    );


                if (
                    pageStatus
                        .locked
                ) {
                    return;
                }


                try {
                    const response =
                        await api.post(
                            "/guestExploreRoutes/click",
                            {
                                page,

                                event_id:
                                    createEventId(),

                                browser:
                                    browserDetails,
                            },
                            {
                                withCredentials:
                                    true,
                            }
                        );


                    applyServerStatus(
                        response.data
                    );
                } catch (error) {
                    console.error(
                        "Explore click tracking failed:",
                        error
                    );


                    if (
                        !mountedRef.current
                    ) {
                        return;
                    }


                    setVerificationFailed(
                        true
                    );

                    setGloballyLocked(
                        true
                    );

                    setExploreGuardReady(
                        true
                    );
                }
            },
            [
                isUnlimited,
                getPageStatus,
                browserDetails,
                applyServerStatus,
            ]
        );


    const captureExploreClick =
        useCallback(
            (
                event,
                page
            ) => {
                /*
                 * Paid tier 1+:
                 * completely bypass this safeguard.
                 */
                if (
                    isUnlimited
                ) {
                    return;
                }


                if (
                    !exploreGuardReady
                ) {
                    event
                        .preventDefault();

                    event
                        .stopPropagation();

                    return;
                }


                const pageStatus =
                    getPageStatus(
                        page
                    );


                if (
                    pageStatus
                        .locked
                ) {
                    event
                        .preventDefault();

                    event
                        .stopPropagation();

                    return;
                }


                /*
                 * Stop the underlying action on the click that
                 * reaches either the page cap or total cap.
                 */
                const nextPageHitsLimit =
                    pageStatus
                        .limit !==
                        null &&
                    pageStatus
                        .clickCount >=
                        pageStatus
                            .limit -
                        1;


                const nextTotalHitsLimit =
                    pageStatus
                        .totalLimit !==
                        null &&
                    pageStatus
                        .totalClickCount >=
                        pageStatus
                            .totalLimit -
                        1;


                if (
                    nextPageHitsLimit ||
                    nextTotalHitsLimit
                ) {
                    event
                        .preventDefault();

                    event
                        .stopPropagation();
                }


                registerExploreClick(
                    page
                );
            },
            [
                isUnlimited,
                exploreGuardReady,
                getPageStatus,
                registerExploreClick,
            ]
        );


    /*
     * Backwards-compatible names are returned too, so existing
     * imports/components do not need a large rewrite.
     */
    return {
        exploreGuardReady,

        guestGuardReady:
            exploreGuardReady,

        accessMode,

        serverTierLevel,

        daily,

        usageDate,

        totalClickCount,

        guestClickCount:
            totalClickCount,

        totalLimit,

        guestLimit:
            totalLimit,

        pageClicks,

        pageLimits,

        globallyLocked,

        isGuestLocked:
            globallyLocked,

        verificationFailed,

        isUnlimited,

        getPageStatus,

        registerExploreClick,

        registerGuestClick:
            registerExploreClick,

        captureExploreClick,

        captureGuestClick:
            captureExploreClick,
    };
};


export default
    useGuestExploreGuard;